const SECTION_SELECTOR = '.shopify-section-products-slider-tabs';
const PAGED_SLIDER_SELECTOR = '.products-slider-tabs__slider-products--paged';
const MOBILE_BREAKPOINT = '(max-width: 768px)';
const TABLET_BREAKPOINT = '(max-width: 992px)';
const SMALL_DESKTOP_BREAKPOINT = '(max-width: 1200px)';

class PagedProductsSliderTabs {
  constructor(slider) {
    this.slider = slider;
    this.scroller = slider.querySelector('slideshow-slides');
    if (!this.scroller) return;

    this.currentEl = slider.querySelector('slideshow-controls [ref="current"]');
    this.totalEl = slider.querySelector('slideshow-controls [ref="total"]');
    this.slides = Array.from(this.scroller.querySelectorAll('slideshow-slide'));
    if (!this.slides.length) return;

    this.slidesPerPage = 1;
    this.totalPages = 1;
    this.rafScrollId = null;
    this.rafRecalcId = null;

    this.prevBtn = slider.querySelector('[ref="previous"]');
    this.nextBtn = slider.querySelector('[ref="next"]');

    this.onScroll = this.onScroll.bind(this);
    this.scheduleRecalc = this.scheduleRecalc.bind(this);
    this.onWheel = this.onWheel.bind(this);

    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
    this.scroller.addEventListener('wheel', this.onWheel, { passive: false });

    slider.previous = (event) => {
      event?.preventDefault();
      const { scrollLeft, clientWidth, scrollWidth } = this.scroller;
      this.scroller.scrollTo({ left: Math.max(0, scrollLeft - clientWidth), behavior: 'smooth' });
    };
    slider.next = (event) => {
      event?.preventDefault();
      const { scrollLeft, clientWidth, scrollWidth } = this.scroller;
      this.scroller.scrollTo({ left: Math.min(scrollWidth - clientWidth, scrollLeft + clientWidth), behavior: 'smooth' });
    };

    this.resizeObserver = new ResizeObserver(this.scheduleRecalc);
    this.resizeObserver.observe(this.scroller);

    this.recalc();
  }

  scheduleRecalc() {
    if (this.rafRecalcId) cancelAnimationFrame(this.rafRecalcId);
    this.rafRecalcId = requestAnimationFrame(() => {
      this.rafRecalcId = null;
      this.recalc();
    });
  }

  getSlidesPerPage() {
    const desktopCount = parseInt(this.slider.dataset.pagedSlidesPerPageDesktop, 10);
    const mobileCount = parseInt(this.slider.dataset.pagedSlidesPerPageMobile, 10);

    if (window.matchMedia(MOBILE_BREAKPOINT).matches) {
      return Number.isFinite(mobileCount) && mobileCount > 0 ? mobileCount : 1;
    }

    if (window.matchMedia(TABLET_BREAKPOINT).matches) {
      return Number.isFinite(desktopCount) && desktopCount > 0 ? Math.max(1, desktopCount - 2) : 1;
    }

    if (window.matchMedia(SMALL_DESKTOP_BREAKPOINT).matches) {
      return Number.isFinite(desktopCount) && desktopCount > 0 ? Math.max(1, desktopCount - 1) : 1;
    }

    return Number.isFinite(desktopCount) && desktopCount > 0 ? desktopCount : 1;
  }

  recalc() {
    const slidesPerPage = this.getSlidesPerPage();

    this.slidesPerPage = slidesPerPage;
    this.totalPages = Math.max(1, Math.ceil(this.slides.length / slidesPerPage));

    const scrollPaddingStart = parseFloat(getComputedStyle(this.scroller).scrollPaddingInlineStart) || 0;
    this.pageSnaps = Array.from({ length: this.totalPages }, (_, i) => {
      const slide = this.slides[i * slidesPerPage];
      return Math.max(0, (slide ? slide.offsetLeft : 0) - scrollPaddingStart);
    });

    const lastIndex = this.slides.length - 1;
    const remainder = this.slides.length % slidesPerPage;
    const lastPageIndex = this.totalPages - 1;
    const lastPageIsPartial = remainder !== 0;

    this.slides.forEach((slide, i) => {
      const pageIndex = Math.floor(i / slidesPerPage);
      const isPageStart = i % slidesPerPage === 0;

      if (i === lastIndex) {
        slide.style.scrollSnapAlign = 'end';
      } else if (isPageStart && !(lastPageIsPartial && pageIndex === lastPageIndex)) {
        slide.style.scrollSnapAlign = 'start';
      } else {
        slide.style.scrollSnapAlign = 'none';
      }
    });

    this.update();
  }

  onWheel(e) {
    if (Math.abs(e.deltaX) >= Math.abs(e.deltaY)) e.preventDefault();
  }

  onScroll() {
    if (this.rafScrollId) return;
    this.rafScrollId = requestAnimationFrame(() => {
      this.rafScrollId = null;
      this.update();
    });
  }

  update() {
    if (!this.currentEl || !this.totalEl) return;

    const scrollLeft = this.scroller.scrollLeft;
    const scrollerWidth = this.scroller.clientWidth;
    const maxScroll = this.scroller.scrollWidth - scrollerWidth;

    let currentPage;
    if (maxScroll > 0 && scrollLeft >= maxScroll - 2) {
      currentPage = this.totalPages;
    } else {
      currentPage = 1;
      if (this.pageSnaps) {
        for (let i = 1; i < this.pageSnaps.length; i++) {
          if (scrollLeft >= this.pageSnaps[i] - 2) currentPage = i + 1;
          else break;
        }
      }
    }

    this.currentEl.textContent = String(currentPage).padStart(2, '0');
    this.totalEl.textContent = String(this.totalPages).padStart(2, '0');

    if (this.prevBtn) this.prevBtn.disabled = currentPage === 1;
    if (this.nextBtn) this.nextBtn.disabled = currentPage === this.totalPages;
  }

  destroy() {
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.scroller?.removeEventListener('wheel', this.onWheel);
    if (this.slider) {
      delete this.slider.previous;
      delete this.slider.next;
    }
    this.resizeObserver?.disconnect();
    if (this.rafScrollId) cancelAnimationFrame(this.rafScrollId);
    if (this.rafRecalcId) cancelAnimationFrame(this.rafRecalcId);
    this.slides?.forEach((slide) => {
      slide.style.removeProperty('scroll-snap-align');
    });
  }
}

const instances = new WeakMap();

function init(root) {
  const scope = root || document;
  scope.querySelectorAll(PAGED_SLIDER_SELECTOR).forEach((slider) => {
    if (instances.has(slider)) return;
    instances.set(slider, new PagedProductsSliderTabs(slider));
  });
}

function teardown(root) {
  const scope = root || document;
  scope.querySelectorAll(PAGED_SLIDER_SELECTOR).forEach((slider) => {
    const instance = instances.get(slider);
    if (instance) {
      instance.destroy();
      instances.delete(slider);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element && event.target.matches(SECTION_SELECTOR)) {
    init(event.target);
  }
});

document.addEventListener('shopify:section:unload', (event) => {
  if (event.target instanceof Element && event.target.matches(SECTION_SELECTOR)) {
    teardown(event.target);
  }
});
